import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Coins, Eye, LogOut, ChevronRight, BookOpen } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API, getCardSymbol } from '../utils/helpers';
import DailyCardWidget from '../components/DailyCardWidget';
import BirthdayBanner from '../components/BirthdayBanner';
import CosmicProfileWidget from '../components/CosmicProfileWidget';
import BirthdaySettings from '../components/BirthdaySettings';
import BadgesWidget from '../components/BadgesWidget';
import ReferralWidget from '../components/ReferralWidget';
import PushNotificationToggle from '../components/PushNotificationToggle';
import PushNotificationModal from '../components/PushNotificationModal';
import StatsWidget from '../components/StatsWidget';
import MarkdownRenderer from '../components/MarkdownRenderer';
import NatalChartWheel from '../components/NatalChartWheel';
import AspectsTable from '../components/AspectsTable';
import SynastryWheel from '../components/SynastryWheel';

export default function DashboardPage() {
  const { user, logout, loading: authLoading } = useAuth();
  const [history, setHistory] = useState([]);
  const [natalHistory, setNatalHistory] = useState([]);
  const [ichingHistory, setIchingHistory] = useState([]);
  const [synastryHistory, setSynastryHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReading, setSelectedReading] = useState(null);
  const [selectedNatalChart, setSelectedNatalChart] = useState(null);
  const [selectedIching, setSelectedIching] = useState(null);
  const [selectedSynastry, setSelectedSynastry] = useState(null);
  const [loadingReading, setLoadingReading] = useState(false);
  const [cardMap, setCardMap] = useState({});
  const [showPushModal, setShowPushModal] = useState(false);
  const [historyTab, setHistoryTab] = useState('tarot');

  useEffect(() => {
    fetch(`${API}/tarot/cards`).then(res => res.json()).then(cards => {
      const map = {}; cards.forEach(c => { map[c.id] = c; }); setCardMap(map);
    }).catch(() => {});
    if (user) {
      Promise.all([
        fetch(`${API}/tarot/history`, { credentials: 'include' }).then(res => res.json()).catch(() => []),
        fetch(`${API}/astrology/natal-chart/history`, { credentials: 'include' }).then(res => res.ok ? res.json() : []).catch(() => []),
        fetch(`${API}/iching/history`, { credentials: 'include' }).then(res => res.ok ? res.json() : []).catch(() => []),
        fetch(`${API}/astrology/synastry/history`, { credentials: 'include' }).then(res => res.ok ? res.json() : []).catch(() => [])
      ]).then(([tarotData, natalData, ichingData, synastryData]) => {
        setHistory(tarotData || []);
        setNatalHistory(natalData || []);
        setIchingHistory(ichingData || []);
        setSynastryHistory(synastryData || []);
        setLoading(false);
      });
      // Apply pending referral
      const pendingRef = sessionStorage.getItem('pending_referral');
      if (pendingRef) {
        sessionStorage.removeItem('pending_referral');
        fetch(`${API}/referral/apply`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ referral_code: pendingRef }) })
          .then(r => r.json()).then(d => { if (d.success) toast.success(d.message); }).catch(() => {});
      }
      // Show push notification modal for new users
      const pushModalShown = localStorage.getItem('push_modal_shown');
      if (!pushModalShown && 'Notification' in window && Notification.permission === 'default') {
        setTimeout(() => setShowPushModal(true), 2000);
      }
    }
  }, [user]);

  const viewReading = async (readingId) => {
    setLoadingReading(true);
    try {
      const res = await fetch(`${API}/tarot/reading/${readingId}`, { credentials: 'include' });
      if (res.ok) setSelectedReading(await res.json());
      else toast.error('No se pudo cargar la lectura');
    } catch (e) { toast.error('Error al cargar la lectura'); }
    finally { setLoadingReading(false); }
  };

  const viewNatalChart = async (chartId) => {
    setLoadingReading(true);
    try {
      const res = await fetch(`${API}/astrology/natal-chart/${chartId}`, { credentials: 'include' });
      if (res.ok) setSelectedNatalChart(await res.json());
      else toast.error('No se pudo cargar la carta natal');
    } catch (e) { toast.error('Error al cargar la carta natal'); }
    finally { setLoadingReading(false); }
  };

  const viewIchingReading = async (readingId) => {
    setLoadingReading(true);
    try {
      const res = await fetch(`${API}/iching/reading/${readingId}`, { credentials: 'include' });
      if (res.ok) setSelectedIching(await res.json());
      else toast.error('No se pudo cargar la lectura de I Ching');
    } catch (e) { toast.error('Error al cargar la lectura'); }
    finally { setLoadingReading(false); }
  };

  const viewSynastry = async (synastryId) => {
    setLoadingReading(true);
    try {
      const res = await fetch(`${API}/astrology/synastry/${synastryId}`, { credentials: 'include' });
      if (res.ok) setSelectedSynastry(await res.json());
      else toast.error('No se pudo cargar la sinastría');
    } catch (e) { toast.error('Error al cargar la sinastría'); }
    finally { setLoadingReading(false); }
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/" />;

  if (selectedSynastry) {
    const syn = selectedSynastry;
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => setSelectedSynastry(null)} className="text-purple-400 hover:text-purple-300 mb-6 text-sm">&larr; Volver al historial</button>
          <div className="glass-card p-6 sm:p-8 rounded-xl border border-pink-400/15 text-center mb-6">
            <h2 className="text-2xl font-bold text-pink-300 mb-1" data-testid="synastry-detail-title">{syn.person_a?.name} ♡ {syn.person_b?.name}</h2>
            <p className="text-[var(--text-muted)] text-sm">{syn.person_a?.sun_sign} × {syn.person_b?.sun_sign}</p>
          </div>
          {syn.person_a?.chart_data && syn.person_b?.chart_data && (
            <div className="glass-card p-6 rounded-xl border border-gold-300/10 mb-6">
              <SynastryWheel chartDataA={syn.person_a.chart_data} chartDataB={syn.person_b.chart_data} crossAspects={syn.cross_aspects || []} nameA={syn.person_a.name} nameB={syn.person_b.name} />
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {[['person_a','amber'],['person_b','cyan']].map(([key, color]) => {
              const person = syn[key];
              return person?.planets?.length > 0 && (
                <div key={key} className={`glass-card p-5 rounded-xl border ${color === 'amber' ? 'border-amber-400/10' : 'border-cyan-400/10'}`}>
                  <h4 className={`text-base font-bold mb-3 ${color === 'amber' ? 'text-amber-400' : 'text-cyan-400'}`}>{person.name} — {person.sun_sign}</h4>
                  <div className="space-y-1">
                    {person.planets.map((p, i) => (
                      <div key={i} className="flex justify-between items-center text-xs py-1 border-b border-white/5 last:border-0">
                        <span className="text-[var(--text-secondary)]">{p.name_es || p.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">{p.sign}</span>
                          <span className="text-purple-400 text-[10px]">{p.position}°</span>
                          {p.retrograde && <span className="text-red-400 text-[10px] font-bold">R</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {syn.cross_aspects?.length > 0 && (
            <div className="glass-card p-6 rounded-xl border border-gold-300/10 mb-6">
              <AspectsTable aspects={syn.cross_aspects} />
            </div>
          )}
          <div className="glass-card p-6 sm:p-8 rounded-xl border border-gold-300/10">
            <MarkdownRenderer content={syn.analysis} className="text-[var(--text-secondary)] text-sm leading-relaxed" />
          </div>
          {syn.credits_used > 0 && <p className="text-sm text-[var(--text-muted)] mt-4 text-center">Créditos utilizados: {syn.credits_used}</p>}
        </div>
      </div>
    );
  }

  if (selectedReading) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => setSelectedReading(null)} className="btn-ghost mb-6">← Volver al historial</button>
          <div className="glass-card p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gold-300 font-heading">{selectedReading.spread_name || selectedReading.spread_type.replace('_', ' ').toUpperCase()}</h2>
              <span className="text-sm text-[var(--text-muted)]">{new Date(selectedReading.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            {selectedReading.cards_info && selectedReading.cards_info.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-bold mb-4 text-purple-300">Cartas de la Lectura</h3>
                <div className="flex flex-wrap gap-4 justify-center">
                  {selectedReading.cards_info.map((card, i) => {
                    const cardId = selectedReading.cards?.[i]?.id;
                    const cardData = cardMap[cardId];
                    const isReversed = selectedReading.cards?.[i]?.reversed;
                    return (
                      <div key={i} className="text-center">
                        <div className={`tarot-card flipped ${isReversed ? 'reversed' : ''}`}>
                          <div className="tarot-card-inner">
                            <div className="tarot-card-back" />
                            <div className="tarot-card-front">
                              {cardData?.image ? <img src={cardData.image} alt={card.card} className="w-full h-full object-cover rounded-lg" /> : <><span className="text-2xl">{getCardSymbol(cardId || i)}</span><p className="text-xs font-bold">{card.card}</p></>}
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-purple-400 mt-2">{card.position}</p>
                        <p className="text-sm font-bold text-white">{card.card}</p>
                        <p className={`text-xs ${card.orientation === 'invertida' ? 'text-red-400' : 'text-green-400'}`}>{card.orientation === 'invertida' ? 'Invertida' : 'Normal'}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="bg-black/30 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-4 text-purple-300">Interpretacion</h3>
              <MarkdownRenderer content={selectedReading.interpretation} className="text-[var(--text-secondary)]" />
            </div>
            {selectedReading.credits_used > 0 && <p className="text-sm text-[var(--text-muted)] mt-4 text-center">Creditos utilizados: {selectedReading.credits_used}</p>}
          </div>
        </div>
      </div>
    );
  }

  // Vista de detalle de carta natal
  if (selectedNatalChart) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => setSelectedNatalChart(null)} className="btn-ghost mb-6">← Volver al historial</button>
          <div className="glass-card p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gold-300 font-heading">Carta Natal: {selectedNatalChart.sun_sign_es}</h2>
                <p className="text-purple-400 text-xs uppercase">{selectedNatalChart.chart_type}</p>
              </div>
              <span className="text-sm text-[var(--text-muted)]">{new Date(selectedNatalChart.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </div>
            <p className="text-[var(--text-muted)] text-sm mb-6">{selectedNatalChart.birth_date} {selectedNatalChart.birth_time && `| ${selectedNatalChart.birth_time}`} {selectedNatalChart.birth_place && `| ${selectedNatalChart.birth_place}`}</p>
            
            {selectedNatalChart.planets?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-3 text-purple-300">Posiciones Planetarias</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {selectedNatalChart.planets.map((p, i) => (
                    <div key={i} className="flex justify-between items-center bg-black/30 rounded-lg px-3 py-2 border border-gold-300/8 min-w-0 overflow-hidden gap-2">
                      <span className="text-[var(--text-secondary)] text-xs sm:text-sm font-medium truncate shrink-0">{p.name}</span>
                      <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                        <span className="text-white font-medium text-xs sm:text-sm truncate">{p.sign}</span>
                        {p.position && <span className="text-purple-400 text-[10px] sm:text-xs shrink-0">{p.position}°</span>}
                        {p.retrograde && <span className="text-red-400 text-[10px] sm:text-xs font-bold shrink-0">R</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {selectedNatalChart.houses?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-3 text-purple-300">Casas Astrologicas</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {selectedNatalChart.houses.map((h, i) => (
                    <div key={i} className="bg-black/30 rounded-lg px-2 py-2 border border-gold-300/8 text-center overflow-hidden">
                      <span className="text-purple-400 text-[10px] sm:text-xs block">Casa {h.number}</span>
                      <span className="text-white font-medium text-xs sm:text-sm truncate block">{h.sign}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {selectedNatalChart.chart_data && (
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-3 text-purple-300">Grafico de la Carta Natal</h3>
                <NatalChartWheel chartData={selectedNatalChart.chart_data} aspects={selectedNatalChart.aspects || []} />
              </div>
            )}

            {selectedNatalChart.aspects && selectedNatalChart.aspects.length > 0 && (
              <AspectsTable aspects={selectedNatalChart.aspects} />
            )}

            <div className="bg-black/30 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-4 text-purple-300">Interpretación</h3>
              <MarkdownRenderer content={selectedNatalChart.analysis} className="text-[var(--text-secondary)]" />
            </div>
            {selectedNatalChart.credits_used > 0 && <p className="text-sm text-[var(--text-muted)] mt-4 text-center">Créditos utilizados: {selectedNatalChart.credits_used}</p>}
          </div>
        </div>
      </div>
    );
  }

  // Vista de detalle de I Ching
  if (selectedIching) {
    const hex = selectedIching.hexagram_data;
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => setSelectedIching(null)} className="btn-ghost mb-6" data-testid="iching-back-btn">← Volver al historial</button>
          <div className="glass-card p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gold-300 font-heading">I Ching: {hex?.primary?.name_es}</h2>
                <p className="text-purple-400 text-xs uppercase">{selectedIching.reading_type}</p>
              </div>
              <span className="text-sm text-[var(--text-muted)]">{new Date(selectedIching.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </div>
            {selectedIching.question && <p className="text-[var(--text-muted)] text-sm italic mb-6">"{selectedIching.question}"</p>}

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-black/30 rounded-xl p-4 text-center">
                <p className="text-4xl mb-2">{hex?.primary?.character}</p>
                <p className="font-bold gold-text font-heading">#{hex?.primary?.number} {hex?.primary?.name_es}</p>
              </div>
              {hex?.has_mutations && hex?.mutated && (
                <div className="bg-black/30 rounded-xl p-4 text-center">
                  <p className="text-xs text-[var(--text-muted)] mb-1">Mutacion</p>
                  <p className="text-4xl mb-2">{hex.mutated.character}</p>
                  <p className="font-bold gold-text font-heading">#{hex.mutated.number} {hex.mutated.name_es}</p>
                </div>
              )}
            </div>

            <div className="bg-black/30 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-4 text-purple-300">Interpretacion</h3>
              <MarkdownRenderer content={selectedIching.interpretation} className="text-[var(--text-secondary)]" />
            </div>
            {selectedIching.credits_used > 0 && <p className="text-sm text-[var(--text-muted)] mt-4 text-center">Creditos utilizados: {selectedIching.credits_used}</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="glass-card p-8 mb-8">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <img src={user.picture || '/avatar.png'} alt={user.name} className="w-24 h-24 rounded-full border-2 border-gold-300/15" />
            <div className="text-center sm:text-left flex-1">
              <h1 className="text-2xl font-bold text-gold-300 font-heading" data-testid="user-name">{user.name}</h1>
              <p className="text-[var(--text-muted)] mb-1">{user.email}</p>
              <BirthdaySettings />
            </div>
            <div className="text-center">
              <div className="credit-badge text-lg px-6 py-2"><Coins className="w-5 h-5" />{user.credits} creditos</div>
              <Link to="/credits" className="text-sm underline mt-2 block text-purple-400">Comprar mas</Link>
            </div>
          </div>
        </div>
        <BirthdayBanner />
        <DailyCardWidget />
        <CosmicProfileWidget />

        {/* User Stats */}
        <StatsWidget />

        {/* Badges & Level */}
        <BadgesWidget />

        {/* Referral Program */}
        <ReferralWidget />

        {/* Push Notifications */}
        <div className="glass-card p-6 mb-8 border border-gold-300/10">
          <h2 className="text-lg font-bold gold-text mb-4 flex items-center gap-2">
            Notificaciones
          </h2>
          <PushNotificationToggle />
        </div>

        {/* Reading History */}
        <div className="glass-card p-8">
          <h2 className="text-xl font-bold text-purple-400 mb-4">Historial</h2>
          
          {/* History Tabs */}
          <div className="flex gap-2 mb-6">
            <button 
              onClick={() => setHistoryTab('tarot')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${historyTab === 'tarot' ? 'bg-purple-500 text-white' : 'bg-white/5 text-[var(--text-muted)] hover:bg-white/10'}`}
            >
              Tarot ({history.length})
            </button>
            <button 
              onClick={() => setHistoryTab('natal')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${historyTab === 'natal' ? 'bg-purple-500 text-white' : 'bg-white/5 text-[var(--text-muted)] hover:bg-white/10'}`}
            >
              Cartas Natales ({natalHistory.length})
            </button>
            <button 
              onClick={() => setHistoryTab('iching')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${historyTab === 'iching' ? 'bg-purple-500 text-white' : 'bg-white/5 text-[var(--text-muted)] hover:bg-white/10'}`}
              data-testid="history-tab-iching"
            >
              I Ching ({ichingHistory.length})
            </button>
            <button 
              onClick={() => setHistoryTab('synastry')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${historyTab === 'synastry' ? 'bg-pink-500 text-white' : 'bg-white/5 text-[var(--text-muted)] hover:bg-white/10'}`}
              data-testid="history-tab-synastry"
            >
              Sinastría ({synastryHistory.length})
            </button>
          </div>

          {loading ? <div className="flex justify-center py-8"><div className="spinner" /></div> : (
            <>
              {/* Tarot History */}
              {historyTab === 'tarot' && (
                history.length === 0 ? (
                  <div className="text-center py-8">
                    <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-[var(--text-muted)]">Aún no tienes lecturas de tarot</p>
                    <Link to="/tarot" className="btn-gold mt-4 inline-block">Comenzar Lectura</Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {history.map((reading, i) => (
                      <motion.div key={reading.reading_id || i} className="p-4 rounded-lg bg-white/5 cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => viewReading(reading.reading_id)} whileHover={{ scale: 1.01 }} data-testid={`history-reading-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-purple-400">{reading.spread_name || reading.spread_type?.replace('_', ' ').toUpperCase()}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-[var(--text-muted)]">{new Date(reading.created_at).toLocaleDateString('es-ES')}</span>
                            <ChevronRight className="w-5 h-5 text-purple-400" />
                          </div>
                        </div>
                        {(reading.cards || []).length > 0 && (
                          <div className="flex gap-2 mb-2 overflow-x-auto py-1">
                            {reading.cards.map((card, j) => {
                              const cd = cardMap[card.id];
                              return cd?.image ? <img key={j} src={cd.image} alt="" className={`w-10 h-16 object-cover rounded border border-gold-300/10 flex-shrink-0 ${card.reversed ? 'rotate-180' : ''}`} /> :
                                <div key={j} className="w-10 h-16 rounded border border-gold-300/10 flex items-center justify-center bg-purple-900/20 flex-shrink-0"><span className="text-sm">{getCardSymbol(card.id)}</span></div>;
                            })}
                          </div>
                        )}
                        <p className="text-[var(--text-secondary)] text-sm line-clamp-2">{reading.interpretation?.substring(0, 150)}...</p>
                      </motion.div>
                    ))}
                  </div>
                )
              )}

              {/* Natal Chart History */}
              {historyTab === 'natal' && (
                natalHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-[var(--text-muted)]">Aún no tienes cartas natales</p>
                    <Link to="/astrology" className="btn-gold mt-4 inline-block">Generar Carta Natal</Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {natalHistory.map((chart, i) => (
                      <motion.div key={chart.natal_chart_id || i} className="p-4 rounded-lg bg-white/5 cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => viewNatalChart(chart.natal_chart_id)} whileHover={{ scale: 1.01 }} data-testid={`history-natal-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="font-bold text-purple-400">Carta Natal: {chart.sun_sign_es}</span>
                            <span className={`ml-2 text-xs px-2 py-0.5 rounded ${chart.chart_type === 'completa' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-[var(--text-muted)]'}`}>
                              {chart.chart_type}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-[var(--text-muted)]">{new Date(chart.created_at).toLocaleDateString('es-ES')}</span>
                            <ChevronRight className="w-5 h-5 text-purple-400" />
                          </div>
                        </div>
                        <p className="text-[var(--text-muted)] text-xs mb-2">{chart.birth_date} {chart.birth_time && `| ${chart.birth_time}`} {chart.birth_place && `| ${chart.birth_place}`}</p>
                        <p className="text-[var(--text-secondary)] text-sm line-clamp-2">{chart.analysis?.substring(0, 150)}...</p>
                      </motion.div>
                    ))}
                  </div>
                )
              )}

              {/* I Ching History */}
              {historyTab === 'iching' && (
                ichingHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-[var(--text-muted)]">Aun no tienes consultas de I Ching</p>
                    <Link to="/iching" className="btn-gold mt-4 inline-block">Consultar el I Ching</Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {ichingHistory.map((reading, i) => (
                      <motion.div key={reading.reading_id || i} className="p-4 rounded-lg bg-white/5 cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => viewIchingReading(reading.reading_id)} whileHover={{ scale: 1.01 }} data-testid={`history-iching-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{reading.hexagram_data?.primary?.character}</span>
                            <div>
                              <span className="font-bold text-purple-400">{reading.hexagram_data?.primary?.name_es}</span>
                              <span className="ml-2 text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-300">{reading.reading_type}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-[var(--text-muted)]">{new Date(reading.created_at).toLocaleDateString('es-ES')}</span>
                            <ChevronRight className="w-5 h-5 text-purple-400" />
                          </div>
                        </div>
                        {reading.question && <p className="text-[var(--text-muted)] text-xs italic mb-2">"{reading.question}"</p>}
                        <p className="text-[var(--text-secondary)] text-sm line-clamp-2">{reading.interpretation?.substring(0, 150)}...</p>
                      </motion.div>
                    ))}
                  </div>
                )
              )}

              {/* Synastry History */}
              {historyTab === 'synastry' && (
                synastryHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-[var(--text-muted)]">Aún no tienes sinastrias</p>
                    <Link to="/synastry" className="btn-gold mt-4 inline-block">Generar Sinastria</Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {synastryHistory.map((syn, i) => (
                      <motion.div key={syn.synastry_id || i} className="p-4 rounded-lg bg-white/5 cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => viewSynastry(syn.synastry_id)} whileHover={{ scale: 1.01 }} data-testid={`history-synastry-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="font-bold text-pink-400">{syn.person_a?.name} ♡ {syn.person_b?.name}</span>
                            <span className="ml-2 text-xs px-2 py-0.5 rounded bg-pink-500/20 text-pink-300">sinastría</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-[var(--text-muted)]">{new Date(syn.created_at).toLocaleDateString('es-ES')}</span>
                            <ChevronRight className="w-5 h-5 text-purple-400" />
                          </div>
                        </div>
                        <p className="text-[var(--text-muted)] text-xs mb-2">{syn.person_a?.sun_sign} × {syn.person_b?.sun_sign}</p>
                        <p className="text-[var(--text-secondary)] text-sm line-clamp-2">{syn.analysis?.substring(0, 150)}...</p>
                      </motion.div>
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </div>
        <div className="text-center mt-8">
          <button onClick={logout} className="btn-ghost" data-testid="dashboard-logout-btn"><LogOut className="w-4 h-4 inline mr-2" />Cerrar Sesion</button>
        </div>
      </div>

      {/* Push Notification Modal */}
      {showPushModal && <PushNotificationModal onClose={() => setShowPushModal(false)} />}
    </div>
  );
}
